import { formatDate } from "@angular/common";
import { Component, EventEmitter, Input, OnChanges, Output } from "@angular/core";
import { ColDef, GridApi, GridOptions, GridReadyEvent, RowNode } from "ag-grid-community";
import { SettingsHistoryResult } from 'src/app/core/settingsHistory/settingsHistory.service';
import { ValueCellComponent } from "../settings-grid/value-cell/value-cell.component";
import { HistoryCellComponent } from "./history-cell/history-cell.component";
import { SettingsService } from '../../core/settings/settings.service';

@Component({
  selector: 'settings-history',
  templateUrl: './settings-history.component.html',
  styleUrls: ['./settings-history.component.scss']
})

export class SettingsHistoryComponent implements OnChanges {

  @Input()
  settingsHistory: SettingsHistoryResult[];

  @Input()
  searchedSetting: string;

  @Output()
  settingHover: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  settingDehover: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  gridReady: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  settingsReady: EventEmitter<void> = new EventEmitter<void>();

  columnDefs: ColDef[] = [
    {
      headerName: 'Name',
      field: 'settingLabel',
      maxWidth: 400
    },
    {
      headerName: 'Value',
      field: 'settingValue',
      maxWidth: 250,
      cellRenderer: HistoryCellComponent
    },
    {
      headerName: 'Date',
      field: 'updatedDate',
      maxWidth: 200,
      comparator: (date1, date2) => {
        if(date1 == undefined && date2 == undefined) {
          return 0;
        }
        else if(date1 == undefined || date2 == undefined) {
          return -1;
        }
        return Date.parse(date1) - Date.parse(date2);
      }
    },
    {
      headerName: 'Username',
      field: 'userName',
      maxWidth: 200
    },
    {
      headerName: 'Notes',
      field: 'notes',
      flex: 1
    }
  ];

  defaultColDef: ColDef = {
    sortable: true,
    filter: false,
    resizable: true,
    suppressMovable: true
  };

  gridOptions: GridOptions = {
    alwaysShowVerticalScroll: true,
    suppressRowHoverHighlight: true,
    suppressRowClickSelection: true,
    suppressLoadingOverlay: true,
    domLayout: "normal",
    components: {
      valueCellRenderer: ValueCellComponent
    }
  };

  displayedSettings: GridSettingsHistory[];

  public gridApi: GridApi<GridSettingsHistory>;

  constructor(private settingsService: SettingsService){}

  async ngOnChanges(): Promise<void> {
    this.displayedSettings = this.settingsHistory?.map<GridSettingsHistory>((s: SettingsHistoryResult) => {
      return {
         updatedDate: formatDate(s.updatedDate, 'medium', 'en-US'),
         settingName: s.settingName,
         settingValue: s.settingValue,
         settingLabel: this.settingsService.getSettingType(s.settingName).label,
         userName: s.userName,
         notes: s.notes,
         isLastKnown: s.isLastKnown,
         authoritativeEntityId: s.entityId
       }
    });

    if (!this.displayedSettings) {
      this.gridApi?.showLoadingOverlay()
    }
  }

  scrollToSetting(settingName: string) {
    const row = this.getLatestRowForSetting(settingName);

    if (!row) return;
    row.setSelected(true);
    this.gridApi.ensureNodeVisible(row, "middle");
  }

  getLatestRowForSetting(setting: string) {
    let latestNode: RowNode<GridSettingsHistory>;
    this.gridApi.forEachNode((n) => {
      if( n.data.settingName === setting
          && (latestNode === undefined || Date.parse(n.data.updatedDate) > Date.parse(latestNode.data.updatedDate))) {
        latestNode = n;
      }
    });

    return latestNode;
  }

  onSettingsUpdated(event: any) {
    if (!this.gridApi) return;
    this.gridApi.forEachNode((n) => {

      n.onMouseEnter = () => {
        n.selectThisNode(true);
        this.settingHover.emit();
      }

      n.onMouseLeave = () => {
        n.setSelected(false);
        this.settingDehover.emit();
      }
    });
    this.gridApi.sizeColumnsToFit();
    this.settingsReady.emit();
  }

  async onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.gridApi.showLoadingOverlay();
    window.addEventListener('resize', function () {
      setTimeout(function () {
        params.api.sizeColumnsToFit();
      });
    });
    this.gridApi.sizeColumnsToFit();
    this.gridReady.emit();
  };
}

interface GridSettingsHistory {
  updatedDate: string;
  settingName: string;
  settingLabel: string;
  settingValue: string;
  userName: string;
  notes: string;
  isLastKnown: boolean;
  authoritativeEntityId: string;
}
